import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { X } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useCoarsePointer } from '@/hooks/use-coarse-pointer';
import { ease, spring } from '@/lib/motion';
import { cn } from '@/lib/utils';

interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  /** Classe do título — sobrescreve o padrão `u-h3`. Usado p/ títulos MONO 12px. */
  titleClassName?: string;
  /** Slot à esquerda do título (ex.: botão voltar). */
  leading?: React.ReactNode;
  /** aria-label explícito quando `title` não é string. */
  ariaLabel?: string;
  children: React.ReactNode;
  className?: string;
  maxWidth?: string;
  hideClose?: boolean;
}

export function BottomSheet({
  open,
  onClose,
  title,
  titleClassName = 'u-h3 text-foreground',
  leading,
  ariaLabel,
  children,
  className,
  maxWidth = 'max-w-lg',
  hideClose = false,
}: BottomSheetProps) {
  const reduce = useReducedMotion();
  // Puxar-pra-fechar só no toque: com mouse o drag do framer bloquearia a seleção de texto.
  const coarse = useCoarsePointer();
  const canDrag = coarse && !reduce;
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !hideClose) onClose();
    }
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => {
      const el = panelRef.current?.querySelector<HTMLElement>(
        'button, input, textarea, select, [tabindex]',
      );
      el?.focus();
    });
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose, hideClose]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[60] flex md:items-center md:justify-center items-end"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2, ease }}
          role="dialog"
          aria-modal="true"
          aria-label={ariaLabel ?? (typeof title === 'string' ? title : undefined)}
        >
          {/* Backdrop — rgba(15,15,20,0.45) do protótipo, com fade */}
          <div
            className="absolute inset-0 bg-[rgba(15,15,20,0.45)]"
            onClick={hideClose ? undefined : onClose}
            aria-hidden
          />
          {/* Painel — bg-card, rounded-t-3xl, slideup, maxHeight ~86vh, safe-area */}
          <motion.div
            ref={panelRef}
            drag={canDrag ? 'y' : false}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.5 }}
            onDragEnd={(_, info) => {
              if (info.offset.y > 100 || info.velocity.y > 500) onClose();
            }}
            initial={reduce ? { opacity: 0 } : { y: '100%', opacity: 1 }}
            animate={reduce ? { opacity: 1 } : { y: 0 }}
            exit={reduce ? { opacity: 0 } : { y: '100%' }}
            transition={spring}
            className={cn(
              // Painel = alvo do drag + altura limitada, SEM scroll próprio (overflow-hidden).
              // O scroll fica no <div> de conteúdo interno → drag e scroll não brigam.
              'relative flex w-full flex-col overflow-hidden rounded-t-3xl bg-card shadow-lift pb-safe max-h-[92vh] md:max-h-[88vh] md:rounded-2xl',
              maxWidth,
              className,
            )}
          >
            {/* Cabeçalho fixo (não rola) com alça centralizada (40x4 rounded-full bg-border) */}
            <div className="shrink-0 px-5 pt-3 pb-2">
              <div className="mx-auto h-1 w-10 rounded-full bg-border" />
              {(title || leading || !hideClose) && (
                <div className="flex items-center justify-between gap-2 pt-2">
                  <div className="flex min-w-0 items-center gap-2">
                    {leading}
                    {title ? <h2 className={titleClassName}>{title}</h2> : <span />}
                  </div>
                  {!hideClose && (
                    <button
                      onClick={onClose}
                      aria-label="Fechar"
                      className="grid h-8 w-8 place-items-center rounded-full bg-surface-2 text-muted-foreground transition active:scale-[0.97] hover:bg-muted"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              )}
            </div>

            <div className="flex-1 overflow-y-auto px-5 pb-5">{children}</div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
